import React, { useEffect, useMemo, useState } from "react";
import type { OnChangeValue } from "react-select";

import { commitMutation, fetchQuery, graphql } from "relay-runtime";

import { useSyncedPersistentStateWithOverride } from "~/actionItems/hooks/useSyncedPersistentState";
import { type ActionItemIntegration, getIntegrationForKey } from "~/integrations/actionItemIntegration";
import { asana, type AsanaStreamIntegrationDetails } from "~/integrations/asana";
import { ReactComponent as AsanaLogo } from "~/legacy-assets/images/logos/asana.svg";
import { environment } from "~/models";
import {
	AddProjectContent,
	type AsanaProjectType,
	type AsanaWorkspaceListType,
	AsanaWorkspaceQuery,
	type ProjectOption,
} from "~/settings/Integrations/Asana/AsanaIntegration";
import type { StreamSettingsResponseStream } from "~/stream/StreamSettings";
import {
	type ActionItemIntegrationCardProps,
	StreamSettingsIntegrationCard,
} from "~/stream/StreamSettings/ActionItemIntegrations";
import { Button, Checkbox } from "~/ui";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import toasty from "~/ui/toast";

import { asanaStore } from "./store";

import type { ActionItemIntegrationsStreamSettingsModalQuery } from "~/__generated__/ActionItemIntegrationsStreamSettingsModalQuery.graphql";
import type { AsanaIntegrationWorkspaceQuery } from "~/__generated__/AsanaIntegrationWorkspaceQuery.graphql";
import type { AsanaStreamSettingsAddProjectStreamMutation as AddAsanaProjectStreamType } from "~/__generated__/AsanaStreamSettingsAddProjectStreamMutation.graphql";

import styles from "./css/styles.module.css";

const AddAsanaProjectStreamMutation = graphql`
	mutation AsanaStreamSettingsAddProjectStreamMutation($input: AddAsanaProjectStreamInput!) {
		addAsanaProjectStream(input: $input) {
			asyncResultId
			noteStream {
				id
				asanaStream {
					asanaProject {
						id
						name
						type
						externalId
						workspaceExternalId
					}
					createdBy {
						fullName
					}
				}
			}
		}
	}
`;

export function AsanaStreamSettingsIntegrationCard({ viewer, onClickIntegration }: ActionItemIntegrationCardProps) {
	const onClick = React.useCallback(() => {
		onClickIntegration(getIntegrationForKey("ASANA"));
	}, [onClickIntegration]);

	return (
		<StreamSettingsIntegrationCard
			integration={asana}
			onClickIntegration={onClick}
			viewer={viewer}
		></StreamSettingsIntegrationCard>
	);
}

interface AsanaBlockProps {
	viewer: ActionItemIntegrationsStreamSettingsModalQuery["response"]["viewer"];
	stream: NonNullable<StreamSettingsResponseStream>;
	onConfigFinish?: (integration: ActionItemIntegration) => void;
	integration?: ActionItemIntegration;
	onCancel: () => void;
	integrationDetails?: AsanaStreamIntegrationDetails;
}

export function AsanaBlock({
	viewer,
	stream,
	onConfigFinish,
	integration,
	onCancel,
	integrationDetails,
}: AsanaBlockProps) {
	const [selectedProject, setSelectedProject] = useSyncedPersistentStateWithOverride<AsanaProjectType | undefined>(
		asanaStore,
		"selectedProject",
		integrationDetails?.projectExternalId
			? {
					id: integrationDetails.projectExternalId,
					name: integrationDetails.name,
					type: integrationDetails.projectType as AsanaProjectType["type"],
				}
			: undefined,
	);
	const [selectedWorkspaceId, setSelectedWorkspaceId] = useSyncedPersistentStateWithOverride<string | undefined>(
		asanaStore,
		"selectedWorkspaceId",
		integrationDetails?.workspaceExternalId,
	);

	const [workspaces, setWorkspaces] = useState<AsanaWorkspaceListType>();
	const [asanaIsConnected] = useState<boolean | undefined>(viewer.integrations.asana?.isConnected);
	const [includeAiGenerated, setIncludeAiGenerated] = useState<boolean>(true);

	const selectedWorkspace = useMemo(() => {
		if (selectedWorkspaceId && workspaces) {
			return workspaces.find(workspace => workspace.externalId == selectedWorkspaceId);
		}
		return undefined;
	}, [selectedWorkspaceId, workspaces]);

	useEffect(() => {
		fetchQuery<AsanaIntegrationWorkspaceQuery>(environment, AsanaWorkspaceQuery, {})
			.toPromise()
			.then(response => {
				const workspaces = response?.viewer.integrations.asana?.workspaces;
				if (workspaces) {
					setWorkspaces(workspaces);
				}
			});
	}, [viewer.integrations.asana, asanaIsConnected]);

	const onSelectedWorkspaceChange = React.useCallback(
		(value: string) => {
			setSelectedWorkspaceId(value);
			setSelectedProject(undefined);
		},
		[setSelectedWorkspaceId, setSelectedProject],
	);

	const onSelectedProjectChange = React.useCallback(
		(option: OnChangeValue<ProjectOption, false>) => {
			if (option) {
				setSelectedProject({ id: option.value, name: option.label, type: "project" });
			} else {
				setSelectedProject(undefined);
			}
		},
		[setSelectedProject],
	);

	const handleIncludeAiGeneratedChange = React.useCallback((checked: boolean) => {
		setIncludeAiGenerated(checked);
	}, []);

	const onAddProjectToStream = React.useCallback(() => {
		const project = selectedProject ? selectedProject : selectedWorkspace;
		if (!project) return;

		commitMutation<AddAsanaProjectStreamType>(environment, {
			mutation: AddAsanaProjectStreamMutation,
			variables: {
				input: {
					id: project.id,
					name: project.name,
					projectType: selectedProject ? "project" : "workspace",
					streamId: stream.id,
					includeAiGenerated,
				},
			},
			onCompleted: ({ addAsanaProjectStream }) => {
				if (addAsanaProjectStream?.noteStream?.asanaStream) {
					if (integration && onConfigFinish) onConfigFinish(integration);
					toasty.success(`Connected Asana project ${addAsanaProjectStream.noteStream.asanaStream.asanaProject.name}`);
				} else {
					toasty.error("Error when adding Asana to this stream");
				}
			},
		});
	}, [selectedProject, selectedWorkspace, stream.id, integration, onConfigFinish, includeAiGenerated]);

	const projectSelect = workspaces ? (
		<div className={styles.column}>
			<AddProjectContent
				onSelectedWorkspaceChange={onSelectedWorkspaceChange}
				onSelectedProjectChange={onSelectedProjectChange}
				selectedProject={selectedProject}
				selectedWorkspace={selectedWorkspace}
				viewer={viewer}
				projectOptional={false}
			/>
			<div className={styles.toggleOption}>
				<Checkbox
					id="include-ai-generated"
					checked={includeAiGenerated}
					onCheckedChange={handleIncludeAiGeneratedChange}
				/>
				<span className={styles.toggleDescription}>Send AI-generated action items to Asana</span>
			</div>
			<span className={styles.asanaText}>
				Action items <b>created after connecting</b> will be sent
			</span>
			<div className={styles.buttonRow}>
				<Button size="normal" onClick={onCancel}>
					Cancel
				</Button>
				<Button
					size="normal"
					variant="primary"
					onClick={onAddProjectToStream}
					disabled={!selectedWorkspace || !selectedProject}
				>
					Connect
				</Button>
			</div>
		</div>
	) : (
		<div>
			Finding workspaces <LoadingSpinner inline size="xs" />
			{/* Note: changed size from "tiny" to "xs" as FontAwesome sizes differ from semantic-ui */}
		</div>
	);

	return (
		<>
			<AsanaLogo />
			<div className={styles.settingsTitle}>Select an Asana project</div>
			<p className={styles.asanaText}>
				Sync action items between the note series <b>{stream?.displayName}</b> and Asana.
			</p>
			{projectSelect}
		</>
	);
}
